import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2, Phone, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FadeUp } from "@/components/anim";
import { CONTACT, BOOKING_URL, SITE_URL } from "@/lib/site";

export default function LandingThankYou() {
  const location = useLocation();
  const name = location.state?.name ? location.state.name.split(" ")[0] : "";

  // Fire conversion events for Google Ads / Meta / GA4. All guarded so nothing breaks
  // if a tag isn't installed. The Google tag (AW-18408526227) is loaded in index.html.
  useEffect(() => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "taster_lead", form: "free-taster" });
      if (typeof window.gtag === "function") {
        // Register a page_view for this SPA route so URL-based conversions can also trigger.
        window.gtag("event", "page_view", {
          page_path: "/kids-dance-classes-whitstable/thank-you",
          page_location: window.location.href,
          page_title: "Free taster — thank you",
        });
        // Google Ads conversion. If you create a dedicated conversion action in Google Ads
        // you'll get a value like "AW-18408526227/AbC-D_efg" — paste it as send_to for precise tracking.
        window.gtag("event", "conversion", { send_to: "AW-18408526227" });
        // GA4 (if you also add a GA4 measurement ID later)
        window.gtag("event", "generate_lead", { event_category: "taster", event_label: "free-taster" });
      }
      if (typeof window.fbq === "function") {
        // Meta Pixel standard Lead event (works automatically once the pixel is installed)
        window.fbq("track", "Lead", { content_name: "Free taster" });
      }
    } catch (e) {
      /* no-op */
    }
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Thank you — your free taster is on its way | Kerry&rsquo;s</title>
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/kids-dance-classes-whitstable/thank-you`} />
      </Helmet>

      {/* Minimal header */}
      <header className="border-b-[0.5px] border-foreground/30" data-testid="thankyou-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-18 flex items-center justify-between py-2">
          <a href="/" className="flex items-baseline gap-3" data-testid="thankyou-logo-link">
            <span className="font-display text-3xl sm:text-4xl leading-none text-foreground">
              KERRY<span className="text-[hsl(var(--kerrys-coral))]">&rsquo;S</span>
            </span>
            <span className="hidden sm:block text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">
              Whitstable
            </span>
          </a>
          <a
            href={CONTACT.phoneHref}
            className="inline-flex items-center gap-2 text-sm font-bold text-foreground hover:text-[hsl(var(--kerrys-sea))] transition-colors"
            data-testid="thankyou-header-phone"
          >
            <Phone className="h-4 w-4" /> {CONTACT.phone}
          </a>
        </div>
      </header>

      <main className="flex-1 grain">
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center" data-testid="thankyou-main">
          <FadeUp>
            <span className="mx-auto h-16 w-16 border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] flex items-center justify-center -rotate-2">
              <CheckCircle2 className="h-8 w-8" />
            </span>
          </FadeUp>
          <FadeUp delay={0.06}>
            <div className="inline-flex items-center gap-2 border-[0.5px] border-foreground bg-card px-3 py-1.5 mt-8">
              <PartyPopper className="h-4 w-4 text-[hsl(var(--kerrys-coral))]" />
              <span className="font-display uppercase text-lg leading-none tracking-[0.02em]">Free taster booked</span>
            </div>
          </FadeUp>
          <FadeUp delay={0.12}>
            <h1 className="font-display uppercase text-6xl sm:text-8xl leading-[0.88] mt-6">
              {name ? `Thank you, ${name}!` : "Thank you!"}
            </h1>
          </FadeUp>
          <FadeUp delay={0.18}>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
              Your details are with Kerry and she&rsquo;ll be in touch very soon to arrange your child&rsquo;s{" "}
              <strong className="text-foreground">free taster class</strong>. Keep an eye on your phone and email
              (do check your junk folder just in case!).
            </p>
          </FadeUp>

          <FadeUp delay={0.24}>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left max-w-2xl mx-auto">
              {[
                { n: "01", t: "We match your class", d: "Kerry finds the perfect class for your child's age and interests." },
                { n: "02", t: "You get the details", d: "We'll text or email you a day, time and venue for the taster." },
                { n: "03", t: "Come and dance", d: "Turn up in comfy clothes — no pressure, just fun." },
              ].map((s) => (
                <div key={s.n} className="border-t-[0.5px] border-foreground pt-4" data-testid="thankyou-step">
                  <p className="font-display text-xl text-[hsl(var(--kerrys-coral))]">{s.n}</p>
                  <p className="font-display uppercase text-2xl leading-[0.95] mt-1">{s.t}</p>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                data-testid="thankyou-book-button"
              >
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  Book online now <ArrowRight className="h-4 w-4 ml-2" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                data-testid="thankyou-call-button"
              >
                <a href={CONTACT.phoneHref}>
                  <Phone className="h-4 w-4 mr-2" /> {CONTACT.phone}
                </a>
              </Button>
            </div>
            <p className="mt-8 text-sm text-muted-foreground">
              Want to look around while you wait?{" "}
              <Link to="/" className="font-bold text-foreground underline underline-offset-4 hover:text-[hsl(var(--kerrys-sea))]" data-testid="thankyou-home-link">
                Explore the full site
              </Link>
            </p>
          </FadeUp>
        </section>
      </main>

      <footer className="bg-[hsl(var(--kerrys-ink))] text-background py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-display text-2xl">
            Kerry<span className="text-[hsl(var(--kerrys-coral))]">&rsquo;s</span>{" "}
            <span className="text-background/50 text-sm align-middle">&middot; Whitstable, Kent</span>
          </p>
          <a href={CONTACT.emailHref} className="text-sm text-background/70 hover:text-background transition-colors">
            {CONTACT.email}
          </a>
        </div>
      </footer>
    </div>
  );
}
